import React, { useEffect, useMemo, useState } from "react";
import { fetchBoqSnapshots } from "../api/productTablesApi";
import { usePresenceOverview } from "../hooks/usePresenceOverview";
import PresenceBadge from "./PresenceBadge";
import { timeAgo } from "../utils/timeAgo";

const STATUS_COLOR = {
  active:    "#22c55e",
  draft:     "#f59e0b",
  completed: "#3b82f6",
  archived:  "#6b7280",
  pending:   "#a78bfa",
  cancelled: "#ef4444",
};
const STATUS_LABEL = {
  active:    "აქტიური",
  draft:     "მუშავდება",
  completed: "დასრულებული",
  archived:  "არქივი",
  pending:   "მოლოდინში",
  cancelled: "გაუქმებული",
};

function sColor(s) { return STATUS_COLOR[(s || "").toLowerCase()] || "#94a3b8"; }
function sLabel(s) { return STATUS_LABEL[(s || "").toLowerCase()] || s || ""; }
const normalize = (v) => String(v || "").toLowerCase();

export default function ProjectSelection({
  projects,
  onSelect,
  onCreate,
  onProfile,
  onSettings,
  onManageTables,
  onControlPanel,
  onLogs,
  onSignOut,
  token,
}) {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  // Latest saved total per project, shown as a chip on each card (cached for the session).
  const [totals, setTotals] = useState(() => {
    try { return JSON.parse(window.sessionStorage.getItem("boq.cardTotals") || "{}"); } catch { return {}; }
  });
  // Who saved the BOQ last and when, per project (same request as the total).
  const [lastSave, setLastSave] = useState(() => {
    try { return JSON.parse(window.sessionStorage.getItem("boq.cardLastSave") || "{}"); } catch { return {}; }
  });
  useEffect(() => {
    if (!token || !Array.isArray(projects)) return undefined;
    let cancelled = false;
    const missing = projects
      .filter((p) => totals[p.id] === undefined || lastSave[p.id] === undefined)
      .slice(0, 25);
    if (!missing.length) return undefined;
    Promise.all(
      missing.map((p) =>
        fetchBoqSnapshots(token, p.id, 1, 0)
          .then((r) => {
            const snap = (r?.items || [])[0];
            return [p.id, snap?.total_cost ?? 0, { by: snap?.created_by_name || "", at: snap?.created_at || "" }];
          })
          .catch(() => [p.id, 0, { by: "", at: "" }])
      )
    ).then((rows) => {
      if (cancelled) return;
      setTotals((prev) => {
        const next = { ...prev, ...Object.fromEntries(rows.map(([id, total]) => [id, total])) };
        try { window.sessionStorage.setItem("boq.cardTotals", JSON.stringify(next)); } catch { /* ignore */ }
        return next;
      });
      setLastSave((prev) => {
        const next = { ...prev, ...Object.fromEntries(rows.map(([id, , info]) => [id, info])) };
        try { window.sessionStorage.setItem("boq.cardLastSave", JSON.stringify(next)); } catch { /* ignore */ }
        return next;
      });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, token]);
  // Who else is working on each project right now (refreshed every 30 s).
  const online = usePresenceOverview(token);
  const [sort, setSort] = useState(() => {
    try { return window.localStorage.getItem("boq.projectSort") || "newest"; } catch { return "newest"; }
  });
  const [pinned, setPinned] = useState(() => {
    try { return JSON.parse(window.localStorage.getItem("boq.pinnedProjects") || "[]"); } catch { return []; }
  });
  const changeSort = (v) => {
    setSort(v);
    try { window.localStorage.setItem("boq.projectSort", v); } catch { /* ignore */ }
  };
  const togglePin = (id) => {
    setPinned((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      try { window.localStorage.setItem("boq.pinnedProjects", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  // Last few projects opened on this computer (newest first).
  const [recentIds, setRecentIds] = useState(() => {
    try { return JSON.parse(window.localStorage.getItem("boq.recentProjects") || "[]"); } catch { return []; }
  });
  const openProject = (project) => {
    setRecentIds((prev) => {
      const next = [project.id, ...prev.filter((id) => id !== project.id)].slice(0, 5);
      try { window.localStorage.setItem("boq.recentProjects", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
    onSelect(project);
  };
  const hasProjects = Array.isArray(projects) && projects.length > 0;

  const statuses = useMemo(() => {
    const s = new Set((projects || []).map((p) => (p.status || "").toLowerCase()).filter(Boolean));
    return Array.from(s);
  }, [projects]);

  const stats = useMemo(() => {
    const byStatus = {};
    (projects || []).forEach((p) => {
      const s = (p.status || "").toLowerCase();
      byStatus[s] = (byStatus[s] || 0) + 1;
    });
    return { total: (projects || []).length, byStatus };
  }, [projects]);

  const filtered = useMemo(() => {
    if (!hasProjects) return [];
    let list = [...projects];
    if (statusFilter !== "all") {
      list = list.filter((p) => (p.status || "").toLowerCase() === statusFilter);
    }
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (p) =>
          normalize(p.name).includes(q) ||
          normalize(p.location).includes(q) ||
          normalize(p.description).includes(q) ||
          normalize(p.customer?.name).includes(q)
      );
    }
    const cmp = {
      newest: (a, b) => Number(b.id) - Number(a.id),
      name: (a, b) => String(a.name || "").localeCompare(String(b.name || ""), "ka"),
      area: (a, b) => (Number(b.area) || 0) - (Number(a.area) || 0),
      status: (a, b) => String(a.status || "").localeCompare(String(b.status || "")),
    }[sort] || (() => 0);
    list.sort(cmp);
    // Pinned projects always float to the top (keeping the chosen order inside each group).
    return [...list.filter((p) => pinned.includes(p.id)), ...list.filter((p) => !pinned.includes(p.id))];
  }, [projects, query, statusFilter, hasProjects, sort, pinned]);

  const navItems = [
    { label: "პროფილი",     fn: onProfile,      color: "var(--accent-primary)" },
    { label: "პარამეტრები", fn: onSettings,      color: "var(--text-muted)" },
    { label: "ბაზები",      fn: onManageTables,  color: "var(--text-muted)" },
    { label: "ადმინი",      fn: onControlPanel,  color: "#a78bfa" },
    { label: "ლოგები",      fn: onLogs,          color: "var(--text-muted)" },
    { label: "გასვლა",      fn: onSignOut,       color: "#f87171" },
  ].filter((item) => typeof item.fn === "function");

  return (
    <div style={{
      minHeight: "100vh", background: "var(--page-gradient)",
      padding: "20px 16px 74px", color: "var(--text-primary)",
      fontFamily: "Arial, sans-serif",
    }}>

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: -0.3 }}>პროექტები</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 3 }}>
            {hasProjects ? `${stats.total} პროექტი` : "პროექტები არ არის"}
          </div>
        </div>
        {typeof onCreate === "function" && (
          <button
            onClick={onCreate}
            style={{
              padding: "9px 16px", borderRadius: 8,
              background: "var(--accent-primary)", border: "none",
              color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer",
              flexShrink: 0,
            }}
          >
            + ახალი
          </button>
        )}
      </div>

      {/* ── Search ── */}
      {hasProjects && (
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="ძიება სახელი, კლიენტი, ადგილი..."
          style={{
            width: "100%", boxSizing: "border-box",
            padding: "9px 12px", borderRadius: 8, fontSize: 13,
            border: "1px solid rgba(148,163,184,0.3)",
            background: "var(--surface-soft)", color: "var(--text-primary)",
            outline: "none", marginBottom: 10,
          }}
        />
      )}

      {/* ── Sort ── */}
      {hasProjects && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <span style={{ fontSize: 11, color: "var(--text-muted)" }}>დალაგება:</span>
          <select
            value={sort}
            onChange={(e) => changeSort(e.target.value)}
            style={{
              flex: 1, padding: "6px 8px", borderRadius: 8, fontSize: 12,
              border: "1px solid var(--field-border)", background: "var(--field-bg)", color: "var(--text-primary)",
            }}
          >
            <option value="newest">ახალი პირველი</option>
            <option value="name">სახელით (ა–ჰ)</option>
            <option value="area">ფართობით</option>
            <option value="status">სტატუსით</option>
          </select>
          {pinned.length > 0 && (
            <span style={{ fontSize: 11, color: "var(--text-muted)" }}>★ {pinned.length}</span>
          )}
        </div>
      )}

      {/* ── Status filter chips ── */}
      {statuses.length > 1 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
          <button
            onClick={() => setStatusFilter("all")}
            style={{
              fontSize: 11, padding: "4px 10px", borderRadius: 10, cursor: "pointer",
              background: statusFilter === "all" ? "var(--accent-primary)" : "var(--surface-soft)",
              color: statusFilter === "all" ? "#fff" : "var(--text-muted)",
              border: `1px solid ${statusFilter === "all" ? "var(--accent-primary)" : "rgba(148,163,184,0.2)"}`,
              fontWeight: 600,
            }}
          >
            ყველა · {stats.total}
          </button>
          {statuses.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(statusFilter === s ? "all" : s)}
              style={{
                fontSize: 11, padding: "4px 10px", borderRadius: 10, cursor: "pointer",
                background: statusFilter === s ? `${sColor(s)}22` : "var(--surface-soft)",
                color: statusFilter === s ? sColor(s) : "var(--text-muted)",
                border: `1px solid ${statusFilter === s ? sColor(s) + "66" : "rgba(148,163,184,0.2)"}`,
                fontWeight: statusFilter === s ? 700 : 400,
              }}
            >
              {sLabel(s)} · {stats.byStatus[s] || 0}
            </button>
          ))}
        </div>
      )}

      {/* ── Recently opened ── */}
      {hasProjects && !query.trim() && statusFilter === "all" && (() => {
        const recent = recentIds
          .map((id) => projects.find((p) => p.id === id))
          .filter(Boolean)
          .slice(0, 4);
        if (recent.length === 0) return null;
        return (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 6 }}>🕘 ბოლოს გახსნილი</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {recent.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => openProject(p)}
                  title={p.name}
                  style={{
                    maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    padding: "4px 10px", borderRadius: 999, fontSize: 12, cursor: "pointer",
                    background: "var(--surface-soft)", border: "1px solid rgba(148,163,184,0.25)",
                    color: "var(--text-primary)",
                  }}
                >
                  {p.name}
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ── Project list ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {filtered.map((project) => {
          const color   = sColor(project.status);
          const label   = sLabel(project.status);
          const dateStr = project.create_date
            ? new Date(project.create_date).toLocaleDateString("ka-GE", {
                year: "numeric", month: "2-digit", day: "2-digit",
              })
            : null;

          return (
            <button
              key={project.id}
              onClick={() => openProject(project)}
              style={{
                width: "100%", textAlign: "left", padding: 0,
                background: "var(--surface-soft)",
                border: "1px solid rgba(148,163,184,0.15)",
                borderRadius: 10, cursor: "pointer", overflow: "hidden",
                display: "flex",
                transition: "border-color 0.15s, box-shadow 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = `${color}55`;
                e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(148,163,184,0.15)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              {/* Left status accent bar */}
              <div style={{ width: 4, background: color, flexShrink: 0 }} />

              {/* Card body */}
              <div style={{ flex: 1, padding: "14px 14px 12px", minWidth: 0 }}>

                {/* Name + status badge */}
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 7 }}>
                  <div style={{
                    fontSize: 15, fontWeight: 700, color: "var(--text-primary)",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    lineHeight: 1.3,
                  }}>
                    {project.name}
                  </div>
                  <span
                    role="button"
                    tabIndex={0}
                    title={pinned.includes(project.id) ? "მოხსნა" : "გამოყოფა"}
                    onClick={(e) => { e.stopPropagation(); togglePin(project.id); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); togglePin(project.id); } }}
                    style={{
                      fontSize: 16, lineHeight: 1, cursor: "pointer", flexShrink: 0,
                      color: pinned.includes(project.id) ? "#f59e0b" : "var(--text-muted)",
                      transition: "transform 0.15s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.3)")}
                    onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                  >
                    {pinned.includes(project.id) ? "★" : "☆"}
                  </span>
                  {project.status && (
                    <span style={{
                      fontSize: 10, padding: "3px 8px", borderRadius: 8, flexShrink: 0,
                      background: `${color}20`, color, border: `1px solid ${color}45`,
                      fontWeight: 700, whiteSpace: "nowrap",
                    }}>
                      {label}
                    </span>
                  )}
                </div>

                {/* Customer + location */}
                {(project.customer?.name || project.location) && (
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
                    {project.customer?.name && (
                      <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                        {project.customer.name}
                      </span>
                    )}
                    {project.customer?.name && project.location && (
                      <span style={{ color: "rgba(148,163,184,0.4)", fontSize: 12 }}>·</span>
                    )}
                    {project.location && (
                      <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                        {project.location}
                      </span>
                    )}
                  </div>
                )}

                {/* Who is working on this project right now */}
                {online[String(project.id)]?.length > 0 && (
                  <div style={{ marginBottom: 6 }}>
                    <PresenceBadge people={online[String(project.id)]} />
                  </div>
                )}

                {/* Building type + area + date */}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  {project.building_type && (
                    <span style={{
                      fontSize: 10, padding: "2px 7px", borderRadius: 6,
                      background: "rgba(148,163,184,0.1)",
                      color: "var(--text-muted)",
                      border: "1px solid rgba(148,163,184,0.15)",
                    }}>
                      {project.building_type}
                    </span>
                  )}
                  {project.area > 0 && (
                    <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                      {Number(project.area).toLocaleString("ka-GE")} მ²
                    </span>
                  )}
                  {totals[project.id] > 0 && (() => {
                    const budget = Number(project.cost_config?.budget) || 0;
                    const over = budget > 0 && totals[project.id] > budget;
                    return (
                      <span
                        title={budget ? `ბიუჯეტი: ${budget.toLocaleString("ka-GE")} ₾` : "ბოლო შენახული ჯამი"}
                        style={{
                          fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6,
                          background: over ? "rgba(251,113,133,0.15)" : "rgba(20,184,196,0.14)",
                          color: over ? "#fb7185" : "var(--accent-primary)",
                        }}
                      >
                        {Math.round(totals[project.id]).toLocaleString("ka-GE")} ₾{over ? " ⚠" : ""}
                      </span>
                    );
                  })()}
                  {dateStr && (
                    <span style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: "auto" }}>
                      {dateStr}
                    </span>
                  )}
                </div>

                {lastSave[project.id]?.at && (
                  <div
                    data-no-translate="true"
                    title={new Date(lastSave[project.id].at).toLocaleString("ka-GE")}
                    style={{ marginTop: 6, fontSize: 11, color: "var(--text-muted)" }}
                  >
                    ✎ ბოლო შენახვა: {lastSave[project.id].by ? `${lastSave[project.id].by} · ` : ""}
                    {timeAgo(lastSave[project.id].at)}
                  </div>
                )}

              </div>
            </button>
          );
        })}
      </div>

      {/* ── Empty ── */}
      {!hasProjects && (
        <div style={{ textAlign: "center", padding: "56px 0" }}>
          <div style={{ fontSize: 40, marginBottom: 14 }}>📁</div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>
            პროექტები არ არის
          </div>
          <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
            "+ ახალი" ღილაკით შექმენით პირველი პროექტი
          </div>
        </div>
      )}

      {hasProjects && filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: "32px 0", fontSize: 13, color: "var(--text-muted)" }}>
          ძიება არ შეესაბამება ვერცერთ პროექტს
        </div>
      )}

      {/* ── Fixed bottom nav ── */}
      <div style={{
        position: "fixed", bottom: 0, left: 0, right: 0,
        background: "var(--nav-bg)",
        borderTop: "1px solid var(--surface-border)",
        display: "flex", justifyContent: "space-around", alignItems: "center",
        padding: "10px 8px 12px",
        zIndex: 20,
      }}>
        {navItems.map(({ label, fn, color }) => (
          <button
            key={label}
            onClick={fn}
            style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              background: "none", border: "none", cursor: "pointer",
              padding: "2px 6px", color, fontSize: 11, fontWeight: 500,
              gap: 1,
            }}
          >
            {label}
          </button>
        ))}
      </div>

    </div>
  );
}
